import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, 
  Upload, 
  Download, 
  Trash2, 
  Edit, 
  Clock, 
  User, 
  MessageSquare,
  CheckCircle,
  AlertCircle,
  FileText,
  Calendar,
  Share,
  Copy,
  ExternalLink,
  Eye
} from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useAuthStore } from '../../store/authStore'
import { getProject, updateProject, deleteProject, generateClientToken } from '../../services/projectService'
import { uploadFile, createVersion, getProjectVersions, getFileUrl, validateFile } from '../../services/fileService'
import { createAuditLog } from '../../services/approvalService'
import { generateApprovalProof } from '../../services/pdfService'
import { getUserOrganizations } from '../../services/organizationService'
import { useConfirm } from '../../hooks/useConfirm'
import { useToast } from '../../hooks/useToast'
import ConfirmDialog from '../../components/ConfirmDialog'
import Toast from '../../components/Toast'
const statusColors = {
  draft: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
  in_review: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  approved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  changes_requested: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  archived: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
}

const statusIcons = {
  draft: FileText,
  in_review: Clock,
  approved: CheckCircle,
  changes_requested: AlertCircle,
  archived: FileText
}

export default function ProjectDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { confirm, confirmState, handleConfirm, handleCancel } = useConfirm()
  const { toastState, success, error: showError, warning, info, closeToast } = useToast()
  const [project, setProject] = useState(null)
  const [versions, setVersions] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentUserRole, setCurrentUserRole] = useState(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [clientLink, setClientLink] = useState('')
  const [editData, setEditData] = useState({
    title: '',
    description: '',
    client_name: '',
    client_email: '',
    status: 'draft'
  })

  useEffect(() => {
    loadProject()
  }, [id])

  const loadProject = async () => {
    try {
      setLoading(true)
      const projectData = await getProject(id)
      setProject(projectData)
      setEditData({
        title: projectData.title,
        description: projectData.description || '',
        client_name: projectData.client_name || '',
        client_email: projectData.client_email || '',
        status: projectData.status
      })

      // Load user role for this organization
      const { data: orgs } = await getUserOrganizations(user.id)
      const currentOrg = orgs?.find(org => org.id === projectData.organization_id)
      if (currentOrg) {
        setCurrentUserRole(currentOrg.memberRole)
      }

      // Load versions
      const versionsData = await getProjectVersions(id)
      setVersions(versionsData || [])
    } catch (error) {
      console.error('Error loading project:', error)
      showError('Failed to load project')
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateProject = async (e) => {
    e.preventDefault()
    
    try {
      await updateProject(id, editData)
      setShowEditModal(false)
      loadProject()
      success('Project updated successfully!')
    } catch (error) {
      console.error('Error updating project:', error)
      showError('Failed to update project: ' + error.message)
    }
  }

  const handleFileSelect = (e) => {
    const file = e.target.files[0]
    if (file) {
      try {
        validateFile(file)
        setSelectedFile(file)
      } catch (error) {
        showError(error.message)
        e.target.value = ''
      }
    }
  }

  const handleUploadVersion = async () => {
    if (!selectedFile) {
      showError('Please select a file')
      return
    }

    try {
      setUploading(true)
      setUploadProgress(30)

      // Upload file
      const { path } = await uploadFile(selectedFile, id)
      setUploadProgress(60)

      // Get next version number
      const nextVersion = versions.length > 0 
        ? Math.max(...versions.map(v => v.version_number)) + 1 
        : 1

      // Create version record
      const version = await createVersion({
        project_id: id,
        version_number: nextVersion,
        file_path: path,
        file_name: selectedFile.name,
        file_size: selectedFile.size,
        file_type: selectedFile.type,
        uploaded_by: user.id
      })
      setUploadProgress(80)

      // Create audit log
      await createAuditLog({
        action_type: 'version_uploaded',
        project_id: id,
        version_id: version.id,
        user_id: user.id,
        metadata: {
          file_name: selectedFile.name,
          file_size: selectedFile.size,
          version_number: nextVersion
        }
      })
      setUploadProgress(100)

      // Update project status to in_review if it was draft
      if (project.status === 'draft') {
        await updateProject(id, { status: 'in_review' })
      }

      // Reload and reset
      await loadProject()
      setShowUploadModal(false)
      setSelectedFile(null)
      setUploadProgress(0)
      success('Version uploaded successfully!')
    } catch (error) {
      console.error('Error uploading version:', error)
      
      // Check if it's a storage limit error
      if (error.code === 'LIMIT_REACHED' && error.limitType === 'storage') {
        const upgrade = await confirm({
          title: 'Storage Limit Reached',
          message: `${error.message}\n\nWould you like to upgrade your plan now?`,
          type: 'warning',
          confirmText: 'Upgrade Now',
          cancelText: 'Maybe Later'
        })
        if (upgrade) {
          window.location.hash = '/subscriptions'
        }
      } else {
        showError('Failed to upload: ' + error.message)
      }
    } finally {
      setUploading(false)
    }
  }

  const handleDownloadVersion = async (version) => {
    try {
      const url = await getFileUrl(version.file_path)
      window.open(url, '_blank')
    } catch (error) {
      console.error('Error downloading file:', error)
      showError('Failed to download file')
    }
  }

  const handleGeneratePDF = async (version) => {
    try {
      // Get approval for version (if exists)
      const { data: approvals } = await supabase
        .from('approvals')
        .select('*')
        .eq('version_id', version.id)
        .eq('status', 'approved')
        .order('approved_at', { ascending: false })
        .limit(1)

      if (approvals && approvals.length > 0) {
        await generateApprovalProof(project, version, approvals[0])
      } else {
        showError('No approval found for this version')
      }
    } catch (error) {
      console.error('Error generating PDF:', error)
      showError('Failed to generate PDF')
    }
  }

  const handleGenerateClientLink = async () => {
    try {
      const token = await generateClientToken(id)
      // Use app.html for React routes
      const link = `${window.location.origin}/app.html#/review/${token}`
      setClientLink(link)
      setShowShareModal(true)
    } catch (error) {
      console.error('Error generating link:', error)
      showError('Failed to generate client link')
    }
  }

  const handleCopyLink = () => {
    navigator.clipboard.writeText(clientLink)
    info('Link copied to clipboard!')
  }

  const handleDeleteProject = async () => {
    const confirmed = await confirm({
      title: 'Delete Project',
      message: 'Are you sure you want to delete this project? This action cannot be undone.',
      type: 'danger',
      confirmText: 'Delete Project',
      cancelText: 'Keep Project'
    })
    
    if (!confirmed) {
      return
    }
    
    try {
      await deleteProject(id)
      success('Project deleted successfully')
      navigate('/projects')
    } catch (error) {
      console.error('Error deleting project:', error)
      showError('Failed to delete project: ' + error.message)
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black dark:border-white mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading project...</p>
        </div>
      </div>
    )
  }

  if (!project) {
    return (
      <div className="p-8">
        <div className="text-center">
          <h2 className="font-display text-2xl font-bold mb-2">Project Not Found</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">This project doesn't exist or you don't have access to it.</p>
          <Link to="/projects" className="text-black dark:text-white underline">
            Back to Projects
          </Link>
        </div>
      </div>
    )
  }

  // Permission checks
  const canEdit = currentUserRole && ['owner', 'admin', 'member'].includes(currentUserRole)
  const canDelete = currentUserRole && ['owner', 'admin'].includes(currentUserRole) || project.created_by === user?.id
  const canUpload = currentUserRole && ['owner', 'admin', 'member'].includes(currentUserRole)
  const isViewer = currentUserRole === 'viewer'

  const StatusIcon = statusIcons[project.status]

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <Link to="/projects" className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white mb-3 sm:mb-4 text-sm sm:text-base">
          <ArrowLeft className="w-4 h-4" />
          Back to Projects
        </Link>
        
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
              <h1 className="font-display text-2xl sm:text-3xl font-bold">{project.title}</h1>
              <span className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium flex items-center gap-1 ${statusColors[project.status]} w-fit`}>
                <StatusIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                {project.status.replace('_', ' ')}
              </span>
            </div>
            {project.description && (
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">{project.description}</p>
            )}
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleGenerateClientLink}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-xl hover:bg-blue-200 dark:hover:bg-blue-900/30 transition-colors text-sm sm:text-base"
            >
              <Share className="w-4 h-4" />
              <span className="hidden sm:inline">Share with Client</span>
              <span className="sm:hidden">Share</span>
            </button>
            <button
              onClick={() => {
                if (!canEdit) {
                  showError('You do not have permission to edit this project')
                  return
                }
                setShowEditModal(true)
              }}
              disabled={!canEdit}
              className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-xl transition-colors text-sm sm:text-base ${
                canEdit
                  ? 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 cursor-pointer'
                  : 'bg-gray-200 dark:bg-gray-900 text-gray-400 dark:text-gray-600 cursor-not-allowed'
              }`}
              title={!canEdit ? 'Only members, admins and owners can edit projects' : ''}
            >
              <Edit className="w-4 h-4" />
              <span>Edit</span>
            </button>
            <button
              onClick={() => {
                if (!canDelete) {
                  showError('You do not have permission to delete this project')
                  return
                }
                handleDeleteProject()
              }}
              disabled={!canDelete}
              className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-xl transition-colors text-sm sm:text-base ${
                canDelete
                  ? 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/30 cursor-pointer'
                  : 'bg-gray-200 dark:bg-gray-900 text-gray-400 dark:text-gray-600 cursor-not-allowed'
              }`}
              title={!canDelete ? 'Only project creator, admins and owners can delete projects' : ''}
            >
              <Trash2 className="w-4 h-4" />
              <span>Delete</span>
            </button>
          </div>
        </div>
      </div>

      {/* Project Info */}
<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
        <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-soft p-4 sm:p-6">
          <div className="flex items-center gap-2 sm:gap-3 mb-2">
            <User className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
            <h3 className="font-semibold text-sm sm:text-base">Client</h3>
          </div>
          <p className="text-base sm:text-lg font-medium break-words">{project.client_name || 'No client set'}</p>
          {project.client_email && (
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">{project.client_email}</p>
          )}
        </div>

        <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-soft p-4 sm:p-6">
          <div className="flex items-center gap-2 sm:gap-3 mb-2">
            <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
            <h3 className="font-semibold text-sm sm:text-base">Versions</h3>
          </div>
          <p className="text-2xl sm:text-3xl font-bold">{versions.length}</p>
        </div>

        <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-soft p-4 sm:p-6">
          <div className="flex items-center gap-2 sm:gap-3 mb-2">
            <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
            <h3 className="font-semibold text-sm sm:text-base">Created</h3>
          </div>
          <p className="text-base sm:text-lg">{formatDate(project.created_at)}</p>
        </div>
      </div>

      {/* Versions */}
      <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-soft p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-0 sm:justify-between mb-4 sm:mb-6">
          <h2 className="font-display text-xl sm:text-2xl font-bold">Versions</h2>
          <button 
            onClick={() => {
              if (!canUpload) {
                showError('You do not have permission to upload files')
                return
              }
              setShowUploadModal(true)
            }}
            disabled={!canUpload}
            className={`flex items-center justify-center gap-2 px-3 sm:px-4 py-2 rounded-xl transition-all text-sm sm:text-base ${
              canUpload
                ? 'bg-black dark:bg-white text-white dark:text-black hover:opacity-90 cursor-pointer'
                : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
            }`}
            title={!canUpload ? 'Only members, admins and owners can upload files' : ''}
          >
            <Upload className="w-4 h-4" />
            <span className="hidden sm:inline">Upload New Version</span>
            <span className="sm:hidden">Upload Version</span>
          </button>
        </div>

        {versions.length === 0 ? (
          <div className="text-center py-8 sm:py-12">
            <Upload className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
            <h3 className="font-semibold text-base sm:text-lg mb-2">No Versions Yet</h3>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-4 px-4">Upload your first file version to get started</p>
            <button 
              onClick={() => {
                if (!canUpload) {
                  showError('You do not have permission to upload files')
                  return
                }
                setShowUploadModal(true)
              }}
              disabled={!canUpload}
              className={`px-5 sm:px-6 py-2.5 sm:py-3 rounded-xl transition-all text-sm sm:text-base ${
                canUpload
                  ? 'bg-black dark:bg-white text-white dark:text-black hover:opacity-90 cursor-pointer'
                  : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              }`}
              title={!canUpload ? 'Only members, admins and owners can upload files' : ''}
            >
              Upload File
            </button>
            {isViewer && (
              <p className="mt-3 text-xs sm:text-sm text-red-600 dark:text-red-400">
                Viewers cannot upload files - contact your admin
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {versions.map((version) => (
              <Link 
                key={version.id} 
                to={`/projects/${id}/versions/${version.id}`}
                className="border border-gray-200 dark:border-gray-700 rounded-xl p-3 sm:p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors block"
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 mb-1">
                      <span className="font-semibold text-sm sm:text-base">Version {version.version_number}</span>
                      {version.file_name && (
                        <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">{version.file_name}</span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(version.created_at)}
                      </span>
                      {version.file_size && (
                        <span>{(version.file_size / 1024 / 1024).toFixed(2)} MB</span>
                      )}
                      <span className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
                        <Eye className="w-3 h-3" />
                        <span className="hidden sm:inline">View Details</span>
                        <span className="sm:hidden">View</span>
                      </span>
                    </div>
                  </div>
                  
                  <button 
                    onClick={(e) => {
                      e.preventDefault()
                      handleDownloadVersion(version)
                    }}
                    className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-sm self-start sm:self-auto"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download</span>
                  </button>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-xl max-w-md w-full p-4 sm:p-6 my-4">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-3 sm:mb-4">Edit Project</h2>
            
            <form onSubmit={handleUpdateProject} className="space-y-3 sm:space-y-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium mb-1">Project Title</label>
                <input
                  type="text"
                  value={editData.title}
                  onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 outline-none text-sm sm:text-base"
                  required
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium mb-1">Description</label>
                <textarea
                  value={editData.description}
                  onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 outline-none text-sm sm:text-base"
                  rows="3"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium mb-1">Client Name</label>
                <input
                  type="text"
                  value={editData.client_name}
                  onChange={(e) => setEditData({ ...editData, client_name: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 outline-none text-sm sm:text-base"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium mb-1">Client Email</label>
                <input
                  type="email"
                  value={editData.client_email}
                  onChange={(e) => setEditData({ ...editData, client_email: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 outline-none text-sm sm:text-base"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium mb-1">Status</label>
                <select
                  value={editData.status}
                  onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 outline-none text-sm sm:text-base"
                >
                  <option value="draft">Draft</option>
                  <option value="in_review">In Review</option>
                  <option value="approved">Approved</option>
                  <option value="changes_requested">Changes Requested</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              <div className="flex gap-2 sm:gap-3 pt-2 sm:pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 px-3 sm:px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-sm sm:text-base"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-3 sm:px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-xl hover:opacity-90 transition-opacity text-sm sm:text-base"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-xl max-w-md w-full p-4 sm:p-6 my-4">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-3 sm:mb-4">Upload New Version</h2>
            
            <div className="space-y-3 sm:space-y-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium mb-2">
                  Select File
                </label>
                <input
                  type="file"
                  onChange={handleFileSelect}
                  accept="image/*,video/*,.pdf"
                  className="w-full px-3 sm:px-4 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl hover:border-gray-400 dark:hover:border-gray-500 transition-colors cursor-pointer text-sm"
                  disabled={uploading}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Supported: Images, Videos, PDF (max 50MB)
                </p>
              </div>

              {selectedFile && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 sm:p-4">
                  <p className="text-xs sm:text-sm font-medium truncate">{selectedFile.name}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              )}

              {uploading && (
                <div>
                  <div className="flex items-center justify-between text-xs sm:text-sm mb-2">
                    <span>Uploading...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-black dark:bg-white h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-2 sm:gap-3 pt-2 sm:pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowUploadModal(false)
                    setSelectedFile(null)
                    setUploadProgress(0)
                  }}
                  className="flex-1 px-3 sm:px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-sm sm:text-base"
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUploadVersion}
                  className="flex-1 px-3 sm:px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 text-sm sm:text-base"
                  disabled={!selectedFile || uploading}
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-xl max-w-lg w-full p-4 sm:p-6 my-4">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-3 sm:mb-4">Share with Client</h2>
            
            <div className="space-y-3 sm:space-y-4">
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                Share this link with your client to allow them to review and approve this project.
              </p>

              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 sm:p-4">
                <div className="flex items-center gap-2 mb-2">
                  <ExternalLink className="w-4 h-4 text-gray-500" />
                  <span className="text-xs sm:text-sm font-medium">Client Review Link</span>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  <input
                    type="text"
                    value={clientLink}
                    readOnly
                    className="flex-1 px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-xs sm:text-sm"
                  />
                  <button
                    onClick={handleCopyLink}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-lg hover:opacity-90 transition-opacity whitespace-nowrap text-sm"
                  >
                    <Copy className="w-4 h-4" />
                    <span>Copy</span>
                  </button>
                </div>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-3 sm:p-4">
                <h4 className="font-medium text-sm sm:text-base text-blue-900 dark:text-blue-300 mb-2">Note:</h4>
                <ul className="text-xs sm:text-sm text-blue-800 dark:text-blue-400 space-y-1">
                  <li>• This link will remain active for 30 days</li>
                  <li>• Clients can view files, leave comments, and approve/request changes</li>
                  <li>• No login required for clients</li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-2 sm:pt-4">
                <button
                  type="button"
                  onClick={() => setShowShareModal(false)}
                  className="flex-1 px-3 sm:px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-sm sm:text-base"
                >
                  Close
                </button>
                <a
                  href={clientLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 px-3 sm:px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-xl hover:opacity-90 transition-opacity text-center text-sm sm:text-base"
                >
                  Preview Link
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmState.isOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        title={confirmState.title}
        message={confirmState.message}
        type={confirmState.type}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
      />

      {/* Toast Notification */}
      <Toast
        isOpen={toastState.isOpen}
        onClose={closeToast}
        message={toastState.message}
        type={toastState.type}
        duration={toastState.duration}
      />
    </div>
  )
}
